import { affiliationService } from "app/services/affiliation.service";
import { useMemo, useState } from "react";
import { USERS_TAB_TO_REFERRAL_TYPE } from "features/affiliation/common/constants";
import {
  AffiliationTabs,
  ExistingUsersStatistics,
  ReferredUsersStatistics,
  UsersStatisticsTypes,
} from "features/affiliation/common/types";
import { getUsdValueWithPrecision } from "features/affiliation/common/utils";
import { getDaysFromDate } from "@shared/utils/date";
import { ReferredUser } from "types/affiliation.types";
import { formatTimeSeriesChartData } from "@shared/utils/charts";
import { TimeRange } from "types";
import { TIME_RANGE_1_DAY } from "common/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";

const useReferredUsers = (
  tab: AffiliationTabs.REFERRED_USERS | AffiliationTabs.EXISTING_USERS,
  /** The referred-users chart's first range (1D unless given). */
  initialTimeRange: TimeRange = TIME_RANGE_1_DAY
) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const [timeRange, setTimeRange] = useState<TimeRange>(initialTimeRange);

  const referralHistoryQuery = affiliationService.useGetReferralHistoryQuery();
  const referredChartQuery = affiliationService.useGetReferredChartQuery({
    time_range: timeRange,
  });
  const referredChartData = referredChartQuery?.data;

  const formattedBarChartData = useMemo(
    () =>
      formatTimeSeriesChartData(
        referredChartData || [],
        undefined,
        undefined,
        undefined
      ),
    [referredChartData]
  );

  const usersData = referralHistoryQuery.data?.data;
  const tiers = referralHistoryQuery.data?.tiers || [];
  const isReferredUsers = tab === AffiliationTabs.REFERRED_USERS;
  const statistics = Object.values(
    isReferredUsers ? ReferredUsersStatistics : ExistingUsersStatistics
  );
  const tableTitle = isReferredUsers
    ? t("users.table_header.referrals")
    : t("users.table_header.promo_payments");

  const filteredUsersByReferralType = useMemo(
    () =>
      usersData?.filter(
        (user) => user.source === USERS_TAB_TO_REFERRAL_TYPE[tab]
      ),
    [usersData, tab]
  );

  const usersFromLastMonth = useMemo(
    () =>
      filteredUsersByReferralType?.filter(
        (user) => getDaysFromDate(user.time, true) <= 30
      ),
    [filteredUsersByReferralType]
  );

  const paidUsers = useMemo(
    () => filteredUsersByReferralType?.filter((user) => user.revenue > 0),
    [filteredUsersByReferralType]
  );

  const numActivePaidUsers = useMemo(
    () => paidUsers?.filter((user) => user.is_active).length || 0,
    [paidUsers]
  );

  const currentTierData = useMemo(() => {
    for (let tier of tiers) {
      if (tier.bottom <= numActivePaidUsers && numActivePaidUsers <= tier.top)
        return tier;
    }
  }, [tiers]);

  const isLastTier = currentTierData?.tier === tiers.length;

  const getTotalEarned = (users?: ReferredUser[]) =>
    users?.reduce((sum, user) => sum + user.commission, 0);

  const getStatisticValue = (
    title: UsersStatisticsTypes,
    users?: ReferredUser[]
  ) => {
    if (
      title === ReferredUsersStatistics.REGISTERED_USERS ||
      title === ExistingUsersStatistics.EXISTING_USERS
    ) {
      return users?.length || 0;
    } else if (
      title === ReferredUsersStatistics.TOTAL_EARNED ||
      title === ExistingUsersStatistics.TOTAL_EARNED
    ) {
      return getUsdValueWithPrecision(getTotalEarned(users) || 0);
    }
  };

  const getStatisticTotalValue = (title: UsersStatisticsTypes) => {
    return getStatisticValue(title, filteredUsersByReferralType);
  };

  const getStatisticLastMonthValue = (title: UsersStatisticsTypes) => {
    return getStatisticValue(title, usersFromLastMonth);
  };

  return {
    referralHistoryQuery,
    tiers,
    currentTierData,
    isLastTier,
    users: filteredUsersByReferralType,
    numActivePaidUsers,
    statistics,
    getStatisticValue,
    tableTitle,
    getStatisticTotalValue,
    getStatisticLastMonthValue,
    referredChartQuery,
    formattedBarChartData,
    timeRange,
    setTimeRange,
  };
};

export default useReferredUsers;
